import React, {
  useEffect,
  useReducer,
  useMemo,
  createContext,
  useContext,
} from "react"
import shopify from "shopify-buy"
import useLocalStorage from "react-use/lib/useLocalStorage"
import { unique, fbTrack } from "../util"

import { Context } from "./MainContext"

export const ShopifyContext = createContext()

const persistedStateId = "marco_checkout"

function createShopifyClient() {
  return shopify.buildClient({
    domain: `${process.env.GATSBY_SHOP_NAME}.myshopify.com`,
    storefrontAccessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  })
}

const shopifyActions = {
  setLoading: "set_loading",
  setCheckout: "set_checkout",
  setProducts: "set_products",
  addBundleItem: "add_bundle_item",
  updateNote: "update_note",
  removeBundleItem: "remove_bundle_item",
  updateBundleItem: "update_bundle_item",
  incrementBundleIndex: "increment_bundle_index",
  decrementBundleIndex: "decrement_bundle_index",
  setBundleIndex: "set_bundle_index",
  setBundleSize: "set_bundle_size",
}

function shopifyCheckoutReducer(state, action) {
  switch (action.type) {
    case shopifyActions.addBundleItem:
      return { ...state, bundle: [...state.bundle, action.payload] }

    case shopifyActions.updateBundleItem:
      const filteredBundle = state.bundle.filter(
        i => i.variantId !== action.payload.variantId
      )
      return { ...state, bundle: [...filteredBundle, action.payload] }

    case shopifyActions.removeBundleItem:
      return {
        ...state,
        bundle: state.bundle.filter(
          i => i.variantId !== action.payload.variantId
        ),
      }

    case shopifyActions.setBundleSize:
      return {
        ...state,
        bundleSize: action.payload,
        bundle: [],
      }

    case shopifyActions.setBundleIndex:
      return {
        ...state,
        bundleIndex: action.payload,
      }

    case shopifyActions.incrementBundleIndex:
      return {
        ...state,
        bundleIndex: state.bundleIndex + 1,
      }

    case shopifyActions.decrementBundleIndex:
      return {
        ...state,
        bundleIndex: state.bundleIndex - 1 < 0 ? 0 : state.bundleIndex - 1,
      }

    case shopifyActions.setProducts:
      return { ...state, products: action.payload }

    case shopifyActions.setLoading:
      return { ...state, loaded: false }

    case shopifyActions.setCheckout:
      const {
        lineItems = [],
        subtotalPrice = 0,
        webUrl = "",
        note,
      } = action.payload

      const bundleCount = lineItems
        .map(i => i.customAttributes[0].value)
        .filter(unique).length

      return {
        ...state,
        note: note || "",
        itemCount: bundleCount,
        lineItems,
        bundleSize: 5,
        subtotalPrice,
        webUrl,
        loaded: true,
        bundle: [],
      }

    default:
      throw new Error(`Action of type ${action.type} does not exist.`)
  }
}

const ShopifyProvider = ({ children }) => {
  const client = useMemo(() => createShopifyClient(), [createShopifyClient]) //eslint-disable-line

  const { setIsCartOpen } = useContext(Context)

  const [shopifyCheckoutId, setShopifyCheckoutId] = useLocalStorage(
    persistedStateId,
    ""
  )
  const [checkout, dispatch] = useReducer(shopifyCheckoutReducer, {
    loaded: false,
    subtotalPrice: 0,
    bundleSize: 5,
    note: "",
    lineItems: [],
    itemCount: 0,
    bundleIndex: 1,
    products: [],
    bundle: [],
    webUrl: "",
  })

  async function handleIncrement(variantId, productId, bundleIndex) {
    fbTrack("track", "CustomizeProduct")
    const item = checkout.bundle.find(
      i => i.variantId === variantId && i.bundleIndex === bundleIndex
    )
    if (item) {
      updateBundleItem(variantId, item.quantity + 1, productId, bundleIndex)
    } else {
      addBundleItem(variantId, 1, productId, bundleIndex)
    }
  }

  async function handleDecrement(variantId, productId, bundleIndex) {
    fbTrack("track", "CustomizeProduct")
    const item = checkout.bundle.find(
      i => i.variantId === variantId && i.bundleIndex === bundleIndex
    )
    if (item) {
      if (item.quantity === 1) {
        removeBundleItem(variantId, bundleIndex)
      } else {
        updateBundleItem(variantId, item.quantity - 1, productId, bundleIndex)
      }
    }
  }

  async function addBundleItem(variantId, quantity, productId, bundleIndex) {
    dispatch({
      type: shopifyActions.addBundleItem,
      payload: { variantId, quantity, bundleIndex, productId },
    })
  }

  async function setBundleSize(bundleSize) {
    dispatch({
      type: shopifyActions.setBundleSize,
      payload: bundleSize,
    })
  }

  async function updateBundleItem(variantId, quantity, productId, bundleIndex) {
    dispatch({
      type: shopifyActions.updateBundleItem,
      payload: { variantId, quantity, bundleIndex, productId },
    })
  }

  async function removeBundleItem(variantId, bundleIndex) {
    dispatch({
      type: shopifyActions.removeBundleItem,
      payload: { variantId, bundleIndex },
    })
  }

  async function addNoteToCheckout(note) {
    const temporalCheckout = await client.checkout.updateAttributes(
      shopifyCheckoutId,
      {
        note,
      }
    )

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
  }

  async function addBundleToCart(bundle) {
    fbTrack("track", "AddToCart")
    setIsCartOpen(true)
    const lineItems = bundle.map(item => ({
      variantId: item.variantId,
      quantity: item.quantity,
      customAttributes: [
        {
          key: "_Bundle",
          value: item.bundleIndex.toString(),
        },
        {
          key: "_Bundle Size",
          value: checkout.bundleSize.toString(),
        },
      ],
    }))

    const temporalCheckout = await client.checkout.addLineItems(
      shopifyCheckoutId,
      lineItems
    )

    dispatch({
      type: shopifyActions.incrementBundleIndex,
    })

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })

    return temporalCheckout
  }

  async function removeBundleFromCart(bundleIndex) {
    const lineItemsToRemove = checkout.lineItems
      .filter(item => item.customAttributes[0].value === bundleIndex.toString())
      .map(item => item.id)

    const temporalCheckout = await client.checkout.removeLineItems(
      shopifyCheckoutId,
      lineItemsToRemove
    )

    dispatch({
      type: shopifyActions.decrementBundleIndex,
    })

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
  }

  function resetCart() {
    setShopifyCheckoutId("")
    dispatch({
      type: shopifyActions.setLoading,
    })
  }

  useEffect(() => {
    if (!client || !client.checkout) return

    async function createNewCheckout() {
      const checkout = await client.checkout.create()
      setShopifyCheckoutId(checkout.id)
      return checkout
    }

    async function fetchProducts() {
      const products = await client.product.fetchAll()
      return products
    }

    function findBundleIndex(lineItems) {
      if (!lineItems) return
      const indexes = lineItems.map(item =>
        Number(item.customAttributes[0].value)
      )
      return Math.max(...indexes, 0) + 1
    }

    async function checkCartExistance() {
      let temporalCheckout = null
      if (shopifyCheckoutId === "") {
        temporalCheckout = createNewCheckout()
      } else {
        temporalCheckout = await client.checkout.fetch(shopifyCheckoutId)
        if (
          temporalCheckout === null ||
          temporalCheckout.completedAt !== null
        ) {
          temporalCheckout = createNewCheckout()
        }
      }

      dispatch({
        type: shopifyActions.setCheckout,
        payload: temporalCheckout,
      })

      dispatch({
        type: shopifyActions.setBundleIndex,
        payload: findBundleIndex(temporalCheckout.lineItems),
      })

      const products = await fetchProducts()
      dispatch({
        type: shopifyActions.setProducts,
        payload: products,
      })
    }

    checkCartExistance()
  }, [shopifyCheckoutId, setShopifyCheckoutId, client])

  return (
    <ShopifyContext.Provider
      value={{
        handleIncrement,
        handleDecrement,
        addBundleToCart,
        addNoteToCheckout,
        removeBundleFromCart,
        resetCart,
        setBundleSize,
        checkout,
      }}
    >
      {children}
    </ShopifyContext.Provider>
  )
}

export default ShopifyProvider
